import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { api } from "../../lib/api";
import ExpandableText from "./ExpandableText";

// Statistiques de visite maison — onglet Aperçu de l'admin. Période au choix
// (aujourd'hui → 1 an) ; TOUTES les pages visitées sont listées (robots exclus).
// [jours, décalage (0 = se termine aujourd'hui, 1 = hier), libellé]
const RANGES = [[1, 0, "Aujourd'hui"], [1, 1, "Hier"], [7, 0, "7 j"], [15, 0, "15 j"],
                [30, 0, "30 j"], [90, 0, "90 j"], [180, 0, "6 mois"], [365, 0, "1 an"]];
const periodLabel = (r, o) =>
  o === 1 && r === 1 ? "hier" : r === 1 ? "aujourd'hui"
  : r === 365 ? "1 an" : r === 180 ? "6 mois" : `${r} derniers jours`;

export default function AnalyticsCard() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);
  const [offset, setOffset] = useState(0);  // 1 = « Hier »

  useEffect(() => {
    let alive = true;
    api.get("/admin/analytics", { params: { days: range, offset, full: 1 } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData((d) => d || false); });
    return () => { alive = false; };
  }, [range, offset]);

  if (!data) return null;  // premier chargement ou erreur (l'ancienne période reste visible pendant le rechargement)
  const maxDay = Math.max(1, ...data.per_day.map((d) => d.n));
  const fr = data.langs.find((l) => l.lang === "fr")?.n || 0;
  const en = data.langs.find((l) => l.lang === "en")?.n || 0;

  return (
    <div className="mt-10 bg-surface border border-ivory/15 rounded-xl p-7" data-testid="analytics-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-sm uppercase tracking-widest text-ivory/60 flex items-center gap-2">
          <Eye className="w-4 h-4 text-gold" /> Visites du site — {periodLabel(range, offset)}
        </h3>
        <p className="font-serif text-2xl text-champagne" data-testid="analytics-total">{data.total_views} <span className="text-sm text-ivory/65 font-sans">pages vues</span></p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-5" data-testid="analytics-range-picker">
        {RANGES.map(([d, o, lab]) => (
          <button
            key={`${d}-${o}`}
            data-testid={o ? "analytics-range-hier" : `analytics-range-${d}`}
            onClick={() => { setRange(d); setOffset(o); }}
            className={`px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${
              range === d && offset === o
                ? "bg-gold text-ink border-gold font-bold"
                : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
            }`}
          >
            {lab}
          </button>
        ))}
      </div>
      <p className="text-xs text-ivory/50 -mt-1 mb-6">Chaque page consultée compte — une même personne qui regarde 10 pages compte 10 fois · robots exclus.</p>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Tendance quotidienne (mini barres) */}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-ivory/65 mb-3">Par jour</p>
          <div className="flex items-end gap-0.5 h-20" data-testid="analytics-chart">
            {data.per_day.map((d) => (
              <div key={d.day} title={`${d.day} : ${d.n}`}
                className="w-full max-w-3.5 bg-gold/70 rounded-t hover:bg-gold transition-colors duration-300"
                style={{ height: `${Math.max(4, (d.n / maxDay) * 100)}%` }} />
            ))}
            {data.per_day.length === 0 && <p className="text-xs text-ivory/65">Aucune visite enregistrée pour l'instant — les statistiques commencent aujourd'hui.</p>}
          </div>
          {(fr + en) > 0 && (
            <p className="text-xs text-ivory/65 mt-3" data-testid="analytics-langs">
              Français {Math.round((fr / (fr + en)) * 100)} % · Anglais {Math.round((en / (fr + en)) * 100)} %
            </p>
          )}
        </div>

        {/* TOUTES les pages visitées (robots exclus) — liste défilante */}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-ivory/65 mb-3">
            Toutes les pages visitées <span className="text-ivory/40 normal-case tracking-normal">({data.top_pages.length})</span>
          </p>
          <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-2" data-testid="analytics-pages" data-lenis-prevent>
            {data.top_pages.map((p, i) => (
              <li key={p.path} className="flex justify-between text-sm gap-3">
                <ExpandableText text={p.label || (p.path === "/" ? "Accueil" : p.path)} testId={`analytics-page-${i}`}
                  className="text-ivory/80 min-w-0 flex-1" />
                <span className="text-gold shrink-0">{p.n}</span>
              </li>
            ))}
            {data.top_pages.length === 0 && <li className="text-xs text-ivory/65">—</li>}
          </ul>
        </div>

        {/* Sources */}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-ivory/65 mb-3">Sources des visiteurs</p>
          <ul className="space-y-1.5" data-testid="analytics-referrers">
            {data.top_referrers.map((r, i) => (
              <li key={r.site} className="flex justify-between text-sm gap-3">
                <ExpandableText text={r.site} testId={`analytics-referrer-${i}`}
                  className="text-ivory/80 min-w-0 flex-1" />
                <span className="text-gold shrink-0">{r.n}</span>
              </li>
            ))}
            {data.top_referrers.length === 0 && <li className="text-xs text-ivory/65">Aucune source externe pour l'instant</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
